import * as cardValidator from 'card-validator';
import { FieldError } from '../common';

export class Validation {
    type : Function;
    message : string;
}

export function Validates(type : any) {
    return (target : any) => {
        ValueValidationRegistry.instance().register(type, target);
    }
}

export class ValueValidationRegistry {
    private validatableClasses : any = {};

    private static _instance = null;

    public static instance(): ValueValidationRegistry {
        if (!this._instance)
            this._instance = new ValueValidationRegistry();

        return this._instance;
    }

    register(type : any, validatable : any) {
        this.validatableClasses[type] = validatable;
    }

    get(type : any) {
        return this.validatableClasses[type];
    }

    getForValue(value : any, type = null): Validatable {
        if (type == null)
            type = value.constructor

        let validatorClass = this.get(value.constructor);

        if (!validatorClass)
            throw new Error(`Cannot find a validator for type ${type.name}`);

        return new validatorClass(value);
    }
}

export interface Validatable {
    validate() : FieldError[] | boolean | string;
}

export abstract class ValueValidator implements Validatable {
    constructor(public value : any) { }
    abstract validate() : FieldError[] | boolean | string;
}

export class NonEmptyValidator extends ValueValidator {
    validate() {
        return (this.value !== null && this.value !== undefined && this.value !== '') || 'This field is required';
    }
}

export class MonthValidator extends ValueValidator {
    validate() {
        if (this.value === null || this.value === undefined || this.value == '')
            return true;

        if (isNaN(parseInt(this.value)))
            return '(1 - 12)';

        if (parseInt(this.value) < 1 || parseInt(this.value) > 12)
            return '(1 - 12)';

        return true;
    }
}

export class DayOfMonthValidator extends ValueValidator {
    validate() {
        if (this.value === null || this.value === undefined || this.value == '')
            return true;

        if (isNaN(parseInt(this.value)))
            return '(1 - 31)';

        if (parseInt(this.value) < 1 || parseInt(this.value) > 31)
            return '(1 - 31)';

        return true;
    }
}

export class YearValidator extends ValueValidator {
    validate() {
        if (this.value === null || this.value === undefined || this.value == '')
            return true;

        if (isNaN(parseInt(this.value)))
            return 'Must be a number';

        if (parseInt(this.value) < 1850)
            return 'Too far in the past';

        return true;
    }
}

export class SixCharacterLength extends ValueValidator {
    validate() {
        return (this.value !== null && this.value !== undefined && this.value.length > 5) ? true : 'Password must be six characters or more';
    }
}

export class SevenCharacterLength extends ValueValidator {
    validate() {
        return (this.value !== null && this.value !== undefined && this.value.length > 6) ? true : 'Password must be seven characters or more';
    }
}

export class Alphanumeric extends ValueValidator {
  validate() {
    return typeof this.value === 'string' && this.value.match(/^[a-zA-Z0-9_]+$/) ?
      true :
      'Must contain only letters, numbers and underscores';
  }
}

export class CreditCardValidator extends ValueValidator {
    validate() {

        let result = cardValidator.number(this.value);
        if (!this.value || !result.isPotentiallyValid)
            return 'Please enter a valid credit card number.';
        return true;
    }
}

export class NonNullValidator extends ValueValidator {
    validate() {
        return this.value !== null && this.value !== undefined;
    }
}

export class EmailValidator extends ValueValidator {
    validate() {
        // please don't make it more complicated than this, you won't get it right anyway
        // https://stackoverflow.com/questions/156430/regexp-recognition-of-email-address-hard

        return ("" + this.value).indexOf("@") > 0 && ("" + this.value).indexOf(".") > 0 ? true : 'Invalid email address';
    }
}

export class DateValidator extends ValueValidator {
    validate() {
        if (/^\d\d?\/\d\d?\/\d\d\d\d$/.test(this.value))
            return true;

        return "Please specify date in MM/DD/YYYY format.";
    }
}

export class OptionalDateValidator extends ValueValidator {
    validate() {
        if (this.value == "")
            return true;

        if (!/^\d\d?\/\d\d?\/\d\d\d\d$/.test(this.value))
            return "Please specify date in MM/DD/YYYY format.";

        return true;
    }
}

export class OptionalFutureDateValidator extends ValueValidator {
    validate() {
        if (this.value == "")
            return true;

        if (!/^\d\d?\/\d\d?\/\d\d\d\d$/.test(this.value))
            return "Please specify date in MM/DD/YYYY format.";

        if (new Date(this.value).toString() == "Invalid Date")
            return "Please specify a valid date.";

        if (new Date(this.value).getTime() < new Date().getTime())
            return "Please specify a date in the future.";

        return true;
    }
}

export class CVCValidator extends ValueValidator {
    validate() {
        let valid = !isNaN(parseInt(this.value))
            && (
                (""+this.value).length == 3
                || (""+this.value).length == 4
            );

        return valid || 'CVC not valid';
    }
}

export class ZipcodeValidator extends ValueValidator {
    validate() {
        let valid = /^\d{5}(\+\d{4})?$/.test(this.value);
        return valid || 'Zip code not valid';
    }
}

export class ExpirationDateValidator extends ValueValidator {
    validate() {
        let str : string = `${this.value}`;
        if (!/^\d\d\/\d\d\d\d$/.test(str))
            return 'Use format "MM/YYYY". eg: for September 2030, enter "09/2030"';

        let result = cardValidator.expirationDate(this.value);

        if (!result.isPotentiallyValid || !result.isValid) {
            try {
                let date = new Date(`${result.year}-${result.month}-01`);
                if (date.getTime() < Date.now())
                    return `Expiration date should be in the future.`;
            } catch (e) {
                console.error(`While attempting to parse expiration date:`);
                console.error(e);
            }

            return `Invalid date`;
        }

        return true;
    }
}

@Validates(Boolean)
export class BooleanValidator extends ValueValidator {
    validate() {
        return this.value === true || this.value === false;
    }
}

@Validates(Number)
export class NumberValidator extends ValueValidator {
    validate() {
        return !isNaN(parseFloat(this.value));
    }
}

@Validates(Boolean)
export class CheckedValidator extends ValueValidator {
    validate() {
        return this.value === true;
    }
}

export class ObjectValidator extends ValueValidator {
    validate() {
        let fieldErrors : FieldError[] = [];
        let validatedFields = this.value.constructor.prototype.__validatedFields

        if (!validatedFields) {
            return;
        }

        for (let key of validatedFields) {
            let value = this.value[key];
            let validationEnabled = Reflect.getMetadata('validation:enabled', this.value, key);
            let validatorType = Reflect.getMetadata('validation:type', this.value, key);

            if (!validationEnabled)
                continue;

            if (validatorType == null) {
                let type = Reflect.getMetadata('design:type', this.value, key);
                validatorType = ValueValidationRegistry.instance().get(type);
            }

            let validatorClasses = [];

            if (length in validatorType)
                validatorClasses = validatorType;
            else
                validatorClasses = [validatorType];

            let options = <ValidationOptions>Reflect.getMetadata('validation:options', this.value, key);
            let message = options ? options.message : null;

            if (options.validateWhen) {
                if (!options.validateWhen(this.value))
                    continue;
            }

            //if (!message)
            //    message = 'This value is invalid';

            let fields = [];

            let field = options ? options.field : null;

            if (options && options.field)
                fields.push(options.field);
            if (options && options.fields)
                fields = fields.concat(options.fields);

            if (fields.length == 0)
                fields.push(key);


            for (let validatorType of validatorClasses) {
                if (!(validatorType instanceof Function)) {
                    console.error('Invalid validator class. Provided validator for '+key+' was:');
                    console.dir(validatorType);
                    continue;
                }

                let validator = new validatorType(value);
                let result = validator.validate();

                if (result !== true) {
                    if (typeof result === 'string')
                        message = result;

                    for (let field of fields) {
                        fieldErrors.push({
                            field,
                            message
                        });
                    }
                }
            }
        }

        if (fieldErrors.length == 0)
            return true;

        return fieldErrors;
    }
}

export interface ValidationOptions {
    message? : string;
    field? : string;
    fields? : string[];
    class? : any;
    validateWhen? : (object : any) => boolean;
}

export function Validated(options : ValidationOptions = null) {

    return (target, propertyKey) => {
        Reflect.defineMetadata(`validation:enabled`, true, target, propertyKey);

        if (!target.__validatedFields)
            target.__validatedFields = [];

        target.__validatedFields.push(propertyKey);

        let cls = options ? options.class : null;

        if (cls != null)
            Reflect.defineMetadata(`validation:type`, cls, target, propertyKey);
        if (options != null)
            Reflect.defineMetadata(`validation:options`, options, target, propertyKey);
    }

}
